import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import Sidebar from './components/Sidebar';
import AdminSidebar, { isAdminArea } from './components/AdminSidebar';
import UserAvatar from './components/UserAvatar';
import BottomNav from './components/BottomNav';
import MobileHome from './components/MobileHome';
import Notifications from './components/Notifications';
import api from './api/axios';
import { timeGreeting } from './utils';
import { isHrOpsPortalView, isHrPortalView, isHrAdminPortalView, isFinancePortalView, isItPortalView, isManagerPortalView, isExoPortalView, isSuperAdminPortalView } from './utils/hrAccess';
import { canRaiseTicket } from './utils/ticketAccess';
import './index.css';
import './motion.css'; // motion & polish layer — remove this line to turn it off
import './responsive.css'; // phone layout layer — remove this line to turn it off
import './mobile-app.css'; // phone Home screen + Attendance logs
import './hrms-polish.css'; // HRMS screens finishing layer — remove to turn off
import './attendance-log.css'; // Daily log on /attendance
import './leave-page.css'; // Leave page: Apply leave / Logs / Rules
import './who-is-in.css'; // Who is in page
import './month-end.css'; // Month end page
import './expenses.css'; // Expenses page
import './leave-approvals.css'; // Leave approvals page
import './leave-admin.css'; // Leave administration page
import './admin-hub.css'; // Admin hub — one page for every settings screen
import './my-tasks.css'; // My Tasks board
import './tickets.css'; // All Tickets / My Tickets board
import './assets.css'; // Asset Management
import './it-dashboard.css'; // IT Admin dashboard + shared ticket panels
import './attendance-setup.css'; // Attendance setup
import './announcements.css'; // Announcements
import './bugs-reports.css'; // Tychi Reports
import './people-admin.css'; // Employees, User Management, Campaigns
import './mobile-polish.css'; // phone layout for redesigned pages (keep last)
import './app-gestures.css'; // swipe, hold-to-confirm, pull-to-refresh, + menu, punch ring
import './attendance-requests.css'; // Who is in: requests waiting on a decision
import './admin-polish.css'; // Admin hub band + readable admin sidebar (keep after admin-hub.css)
import './profile-polish.css'; // profile screen (My details + HR's employee profile)

// Public pages stay eager — they are the first paint
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import CostApproval from './pages/CostApproval';
import SSOCallback from './pages/SSOCallback';
import LoginAppBg from './components/LoginAppBg';

/**
 * PERF-02: authenticated pages are route-split so users no longer download
 * the whole app up front. Each page becomes its own chunk fetched on demand.
 */
const { lazy, Suspense } = React;

const PortalDashboardRouter = lazy(() => import('./pages/PortalDashboardRouter'));
const Profile = lazy(() => import('./pages/Profile'));
const MyTasks = lazy(() => import('./pages/MyTasks'));
const Reports = lazy(() => import('./pages/Reports'));
const Announcements = lazy(() => import('./pages/Announcements'));

const RaiseTicket = lazy(() => import('./pages/RaiseTicket'));
const TicketList = lazy(() => import('./pages/TicketList'));

const Employees = lazy(() => import('./pages/Employees'));
const MyTeam = lazy(() => import('./pages/MyTeam'));
const OrgChart = lazy(() => import('./pages/OrgChart'));
const EmployeeDetail = lazy(() => import('./pages/EmployeeDetail'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const MyForms = lazy(() => import('./pages/MyForms'));
// Named export — lazy() needs a module with a default
const FormFill = lazy(() => import('./pages/MyForms').then((m) => ({ default: m.FormFill })));
const Referrals = lazy(() => import('./pages/Referrals'));
const Bugs = lazy(() => import('./pages/Bugs'));
const ReferralAdmin = lazy(() => import('./pages/ReferralAdmin'));

const Assets = lazy(() => import('./pages/Assets'));
const MyAssets = lazy(() => import('./pages/MyAssets'));
const AssetAudit = lazy(() => import('./pages/AssetAudit'));
const Leave = lazy(() => import('./pages/Leave'));
const Expenses = lazy(() => import('./pages/Expenses'));
const LeaveApprovals = lazy(() => import('./pages/LeaveApprovals'));
const LeaveAdmin = lazy(() => import('./pages/LeaveAdmin'));
const Attendance = lazy(() => import('./pages/Attendance'));
const AttendanceLogs = lazy(() => import('./pages/AttendanceLogs'));
const AttendanceTeam = lazy(() => import('./pages/AttendanceTeam'));
const AttendanceAdmin = lazy(() => import('./pages/AttendanceAdmin'));
const AttendanceMonth = lazy(() => import('./pages/AttendanceMonth'));
const AttendanceQr = lazy(() => import('./pages/AttendanceQr'));
const Users = lazy(() => import('./pages/Users'));
const Settings = lazy(() => import('./pages/Settings'));
const AdminHub = lazy(() => import('./pages/AdminHub'));
const EmployeeProfilePage = lazy(() => import('./pages/EmployeeProfilePage'));
const Audit = lazy(() => import('./pages/Audit'));
const FormBuilder = lazy(() => import('./pages/FormBuilder'));
const WorkflowEditor = lazy(() => import('./pages/WorkflowEditor'));
const WorkflowBoard = lazy(() => import('./pages/OnboardingBoard'));
const AddressVerification = lazy(() => import('./pages/AddressVerification'));

const RouteFallback = () => (
  <div className="page" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
    <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</div>
  </div>
);

const LandingRedirect = () => {
  useEffect(() => {
    window.location.replace('/landing.html');
  }, []);
  return null;
};

const HR_OPS_PATHS = ['/dashboard', '/my-tasks', '/announcements', '/my-tickets', '/my-assets', '/my-team', '/org-chart', '/raise-ticket', '/employees', '/onboarding', '/offboarding', '/campaigns', '/profile'];
const HR_REFERRAL_PATHS = [...HR_OPS_PATHS, '/referrals-admin'];
const SUPER_ADMIN_PATHS = [
  '/dashboard', '/announcements',
  '/employees', '/onboarding', '/offboarding', '/campaigns', '/referrals-admin',
  '/assets', '/my-assets', '/my-forms', '/my-team', '/org-chart', '/profile',
  '/my-tickets', '/all-tickets',
  '/settings', '/workflows', '/audit',
];

const AppLayout = () => {
  const { user, can, logout, perms, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openBadge, setOpenBadge] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  /* Which menu to show. The list of admin routes lives with the admin menu,
     so adding a screen there does not mean remembering to edit this file. */
  const inAdminArea = isAdminArea(location.pathname);
  const isAdminView = user?.role === 'admin';
  const isHrOpsView = isHrOpsPortalView(user);
  const isHrView = isHrPortalView(user);
  const isHrAdminView = isHrAdminPortalView(user);
  const isFinanceView = isFinancePortalView(user);
  const isItPortalViewUser = isItPortalView(user);
  const isManagerView = isManagerPortalView(user);
  const isExoView = isExoPortalView(user);
  const isSuperAdminView = isSuperAdminPortalView(user);

  useEffect(() => {
    if (!isExoView) return;
    const path = location.pathname;
    const allowed = ['/profile', '/my-tasks', '/reports'];
    if (can('dashboard')) allowed.push('/dashboard');
    if (can('tickets')) allowed.push('/my-tickets', '/raise-ticket');
    if (can('employees')) allowed.push('/employees', '/onboarding', '/offboarding');
    if (can('assets')) allowed.push('/assets', '/my-assets');
    if (can('address_verification')) allowed.push('/campaigns');
    if (can('reports')) allowed.push('/reports');
    const ok = allowed.some((p) => path === p || path.startsWith(`${p}/`));
    if (!ok) {
      navigate(can('reports') ? '/reports' : '/my-tasks', { replace: true });
    }
  }, [isExoView, location.pathname, navigate, perms]);

  useEffect(() => {
    if (!isSuperAdminView || isExoView) return;
    const path = location.pathname;
    const ok = SUPER_ADMIN_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
    if (!ok) {
      navigate('/dashboard', { replace: true });
    }
  }, [isSuperAdminView, isExoView, location.pathname, navigate]);

  useEffect(() => {
    if (!isHrOpsView || isExoView || isSuperAdminView) return;
    const path = location.pathname;
    const allowed = [...(isHrView ? HR_REFERRAL_PATHS : HR_OPS_PATHS)];
    if (can('assets')) allowed.push('/assets', '/assets/audit');
    if (can('reports')) allowed.push('/reports');
    // Without this the route would render and this effect would immediately
    // redirect away from it — the page would flash and vanish.
    if (can('referrals')) allowed.push('/referrals-admin');
    // Same trap: HR portal users have leave_admin by default, so without these
    // the leave pages would render and this effect would bounce them straight
    // back out.
    if (can('leave')) allowed.push('/leave', '/leave-approvals', '/attendance-team');
    if (can('attendance')) allowed.push('/attendance');
    /*
     * Expenses was never in the HR path list, so an HR user who opened it was
     * redirected straight back to the dashboard — the page existed, the
     * permission was granted, and the screen still bounced.
     */
    if (can('expenses') || can('expenses_admin')) allowed.push('/expenses');
    allowed.push('/my-forms', '/admin');
    if (can('leave_admin')) allowed.push('/leave-admin', '/attendance-admin', '/attendance-month', '/attendance-qr');
    const ok = allowed.some((p) => path === p || path.startsWith(`${p}/`));
    if (!ok) {
      navigate(can('dashboard') ? '/dashboard' : '/profile', { replace: true });
    }
  }, [isHrOpsView, isHrView, isExoView, isSuperAdminView, location.pathname, navigate, perms]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  /**
   * Escape closes the topmost modal (UX-01).
   * Every overlay in the app already closes via `e.target === e.currentTarget`,
   * so dispatching a click on the overlay itself reuses each modal's own close
   * logic — including modals that intentionally block closing while saving.
   */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      const overlays = document.querySelectorAll('.modal-overlay');
      if (!overlays.length) return;
      const topmost = overlays[overlays.length - 1];
      topmost.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!user) return;
    api
      .get('/tickets/stats')
      .then((r) => {
        /*
         * One definition of open, for everybody: not Resolved or Closed.
         *
         * An admin's badge used to count only the literal 'Open' status, so a
         * ticket somebody had started work on vanished from the badge while
         * still sitting in the queue. Employees already added In Progress by
         * hand, which is the same sum the server now returns outright.
         */
        setOpenBadge(parseInt(
          r.data.unresolved
            ?? (parseInt(r.data.open || 0, 10) + parseInt(r.data.in_progress || 0, 10)),
          10
        ));
      })
      .catch(() => {});
  }, [user, isAdminView]);

  useEffect(() => {
    if (!isAdminView) return;
    if (location.pathname.startsWith('/my-assets')) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAdminView, location.pathname, navigate]);

  // SEC-06: on first load we don't yet know if the httpOnly session cookie
  // is valid — wait for that check instead of bouncing straight to /login.
  if (loading) return <RouteFallback />;
  if (!user) return <Navigate to="/login" replace />;

  /*
   * Who may open the Admin area. Read by the topbar button and by the /admin
   * route, so a visible button always leads somewhere — the two drifting apart
   * is how "I can see it but it sends me to the dashboard" happens.
   *
   * leave_admin is in the list because HR held it before these two switches
   * existed; dropping it would take the page away from the one person who runs
   * leave and attendance.
   */
  const canOpenAdmin = isAdminView || isSuperAdminView
    || can('admin_partial') || can('admin_full') || can('leave_admin');

  const adminOnly = (el) => (isAdminView ? el : <Navigate to="/dashboard" />);
  const adminOrSuperAdmin = (el) => ((isAdminView || isSuperAdminView) ? el : <Navigate to="/dashboard" />);
  /*
   * Users & access and Organisation settings — the two screens Full access to
   * the Admin page opens. Mirrors adminOrFullPanel on the server; when these
   * two disagreed, the card appeared and the page bounced.
   */
  const adminOrFullPanel = (el) => (
    (isAdminView || isSuperAdminView || can('admin_full')) ? el : <Navigate to="/dashboard" />
  );

  const portalBadge = isAdminView
    ? { label: 'IT Admin', bg: '#e0f2f1', color: '#00796b' }
    : isSuperAdminView
    ? { label: 'Super Admin', bg: '#e0f2fe', color: '#0e7490' }
    : isExoView
      ? { label: 'EXO', bg: '#fef3c7', color: '#b45309' }
      : isHrAdminView
      ? { label: 'HR Admin', bg: '#fce8e6', color: '#d93025' }
      : isHrOpsView
        ? { label: 'HR', bg: '#dbeafe', color: '#1d4ed8' }
        : isFinanceView
          ? { label: 'Finance', bg: '#f3e5f5', color: '#7b1fa2' }
          : isItPortalViewUser
            ? { label: 'IT Support', bg: '#e0f2f1', color: '#00796b' }
            : isManagerView
              ? { label: 'Manager', bg: '#e0e7ff', color: '#4338ca' }
            : null;

  /*
   * Routes this person would be redirected away from. Worked out once and
   * given to both the home screen and the bottom bar, so neither offers a
   * destination the router bounces — which reads as broken rather than as the
   * rule it is.
   */
  const bouncedRoutes = [
    isAdminView && '/my-assets',
    isHrOpsView && '/my-forms',
    (isHrOpsView || isSuperAdminView) && '/referrals',
  ].filter(Boolean);

  return (
    <div className="app-layout">
      <div className="app-scene-bg" aria-hidden>
        <LoginAppBg className="app-scene-bg-art" />
        <div className="app-scene-overlay" />
      </div>
      {/*
        One menu at a time. Inside the Admin area its own menu replaces the
        main one — the admin screens are not in the main menu any more, so
        showing both would be two places to look for the same link.
      */}
      {inAdminArea
        ? <AdminSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        : <Sidebar openBadge={openBadge} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
      <div
        className={`sidebar-overlay${sidebarOpen ? ' show' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      <BottomNav hidden={bouncedRoutes} />
      <div className="main-content">
        <div className="topbar">
          <button
            className="topbar-menu-btn"
            type="button"
            title="Menu"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          >
            <i className="ti ti-menu-2"></i>
          </button>
          <div className="topbar-greeting">
            <div className="topbar-greeting-row">
              <span className="topbar-greeting-label">{timeGreeting()},</span>
              <span className="topbar-greeting-name">{user?.name?.split(' ')[0]}</span>
              {portalBadge && (
                <span
                  className="topbar-role-badge"
                  style={{ background: portalBadge.bg, color: portalBadge.color }}
                >
                  {portalBadge.label}
                </span>
              )}
            </div>
          </div>
          <div className="topbar-right">
            <div className="topbar-actions">
              {/*
                The way in to the Admin area, next to the bell rather than in
                the menu. The admin screens have their own sidebar now, so a
                link to them in the main menu was a menu item that replaced the
                menu — and inside the area the same slot is the way back out.

                The test is the one the /admin route uses, so the button cannot
                appear for somebody the router would bounce.
              */}
              {canOpenAdmin && (
                /*
                  Labelled, not an icon on its own. An unfamiliar glyph in a
                  row of other glyphs is a guess — and this is the only way in
                  to a whole area of the app, so it has to say so.
                */
                <button
                  className={`topbar-admin-btn${inAdminArea ? ' active' : ''}`}
                  type="button"
                  title="Admin Panel"
                  onClick={() => navigate('/admin')}
                >
                  <i className="ti ti-layout-grid"></i>
                  <span>Admin Panel</span>
                </button>
              )}
              <Notifications />
              {(isAdminView || isSuperAdminView) && (
                <button
                  className="topbar-icon-btn"
                  type="button"
                  title="Settings"
                  onClick={() => navigate('/settings')}
                >
                  <i className="ti ti-settings"></i>
                </button>
              )}
              {/* The same picture the profile page shows. It used to be
                  initials here regardless, so one person was a photo in one
                  corner and two letters in another. */}
              <div onClick={() => navigate('/profile')} title="Profile"
                style={{ cursor: 'pointer', display: 'flex' }}>
                <UserAvatar className="topbar-avatar" alt=""
                  initials={user?.name?.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '??'} />
              </div>
            </div>
            <button
              type="button"
              className="topbar-logout-btn"
              title="Logout"
              aria-label="Logout"
              onClick={logout}
            >
              <i className="ti ti-logout" />
            </button>
          </div>
        </div>

        <div className="main-outlet">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Core */}
          <Route path="/dashboard" element={
            can('dashboard') ? (<>
              <MobileHome hidden={bouncedRoutes} />
              {/* The desktop dashboard, hidden on phones. MobileHome is the
                  whole home screen there — the stat cards underneath it were
                  a second home screen stacked on the first, saying the same
                  things in a layout built for a mouse. */}
              <div className="dash-desktop-only">
                <PortalDashboardRouter />
              </div>
            </>) : <Navigate to={isExoView ? (can('reports') ? '/reports' : '/my-tasks') : '/profile'} />
          } />
          <Route path="/profile" element={<Profile />} />
          {/* HR opening somebody else's profile. Same component as the "My
              details" tab, so the two cannot drift apart — the only
              difference is whose record it loads. The server checks the
              employee_details permission on the way in. */}
          <Route path="/profile/:employeeId" element={<EmployeeProfilePage />} />
          <Route path="/announcements" element={<Announcements />} />
          <Route path="/my-tasks" element={can('my_tasks') ? <MyTasks /> : <Navigate to={isExoView ? '/reports' : '/dashboard'} />} />
          <Route path="/reports" element={can('reports') ? <Reports /> : <Navigate to="/dashboard" />} />

          {/* Tickets */}
          <Route
            path="/raise-ticket"
            element={
              canRaiseTicket(user, can)
                ? <RaiseTicket />
                : <Navigate to={isAdminView ? '/all-tickets' : '/dashboard'} replace />
            }
          />
          <Route path="/my-tickets" element={can('tickets') ? <TicketList /> : <Navigate to="/dashboard" />} />
          <Route path="/all-tickets" element={(isAdminView || isSuperAdminView) && can('tickets') ? <TicketList adminView /> : <Navigate to="/my-tickets" replace />} />

          {/* HR */}
          <Route path="/employees" element={can('employees') ? <Employees /> : <Navigate to="/dashboard" />} />
          <Route path="/my-team" element={<MyTeam />} />
          {/*
            No client-side permission check: the API decides who may see the
            chart, and it checks the database for direct reports rather than
            trusting anything the browser claims. The page renders the 403 it
            gets back. A guard here as well would only be a second, weaker copy
            of the same rule, and the two would drift apart.
          */}
          <Route path="/org-chart" element={<OrgChart />} />
          {/* The list and the record are separate permissions: someone can be given
              the directory without being given everybody's personal file. */}
          <Route path="/employees/:id" element={can('employee_details') ? <EmployeeDetail /> : <Navigate to="/employees" />} />
          <Route path="/onboarding" element={(isHrOpsView || isSuperAdminView) && can('employee_details') ? <WorkflowBoard mode="onboarding" /> : <Navigate to="/dashboard" />} />
          <Route path="/offboarding" element={(isHrOpsView || isSuperAdminView) && can('employee_details') ? <WorkflowBoard mode="offboarding" /> : <Navigate to="/dashboard" />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/referrals" element={(isHrOpsView || isSuperAdminView) ? <Navigate to="/dashboard" replace /> : <Referrals />} />
          {/* Tychi Reports — Super Admin only. Everyone else raises product
              issues as ordinary tickets under the product categories. */}
          <Route path="/bugs" element={(isSuperAdminView || isAdminView) ? <Bugs /> : <Navigate to="/dashboard" replace />} />
          {/* Hr Operation and Super Admin by role; anyone else via the referrals switch */}
          <Route path="/referrals-admin" element={(isHrView || isSuperAdminView || can('referrals')) ? <ReferralAdmin /> : <Navigate to="/dashboard" replace />} />
          <Route path="/my-forms" element={isHrOpsView ? <Navigate to="/dashboard" replace /> : <MyForms />} />
          <Route path="/my-forms/:assignmentId" element={isHrOpsView ? <Navigate to="/dashboard" replace /> : <FormFill />} />

          {/* Admin */}
          <Route path="/my-assets" element={isAdminView ? <Navigate to="/dashboard" replace /> : <MyAssets />} />
          {/* Leave is for people, not department accounts — a shared mailbox has
              no employee record and therefore no balance of its own. */}
          <Route path="/leave" element={can('leave') ? <Leave /> : <Navigate to="/dashboard" replace />} />
          {/* Anybody who can claim, or anybody who decides claims — the second
              without the first would be an HR person unable to see the page
              they are supposed to work from. */}
          <Route path="/expenses" element={
            (can('expenses') || can('expenses_admin'))
              ? <Expenses />
              : <Navigate to="/dashboard" replace />
          } />
          <Route path="/leave-approvals" element={can('leave') ? <LeaveApprovals /> : <Navigate to="/dashboard" replace />} />
          <Route path="/leave-admin" element={can('leave_admin') ? <LeaveAdmin /> : <Navigate to="/dashboard" replace />} />
          <Route path="/attendance" element={can('attendance') ? <Attendance /> : <Navigate to="/dashboard" replace />} />
          <Route path="/attendance-logs" element={can('attendance') ? <AttendanceLogs /> : <Navigate to="/dashboard" replace />} />
          <Route path="/attendance-team" element={(can('attendance') && can('leave')) ? <AttendanceTeam /> : <Navigate to="/dashboard" replace />} />
          <Route path="/attendance-admin" element={can('leave_admin') ? <AttendanceAdmin /> : <Navigate to="/dashboard" replace />} />
          <Route path="/attendance-month" element={can('leave_admin') ? <AttendanceMonth /> : <Navigate to="/dashboard" replace />} />
          <Route path="/attendance-qr/:id" element={can('leave_admin') ? <AttendanceQr /> : <Navigate to="/dashboard" replace />} />
          {/* Before /assets so the tab is not read as an asset id */}
          <Route path="/assets/audit" element={can('assets') ? <AssetAudit /> : <Navigate to="/dashboard" replace />} />
          <Route path="/assets" element={can('assets') ? <Assets /> : <Navigate to="/dashboard" />} />
          <Route path="/users" element={adminOrFullPanel(<Users />)} />
          {/*
            Deliberately not admin-only. HR holds leave_admin and has real
            settings to manage, so locking the hub to platform admins would
            leave the one person who runs leave and attendance unable to open
            the page that lists them. The cards inside do the real filtering.
          */}
          <Route path="/admin" element={
            canOpenAdmin ? <AdminHub /> : <Navigate to="/dashboard" replace />
          } />
          <Route path="/settings" element={adminOrFullPanel(<Settings />)} />
          <Route path="/settings/forms/:id/edit" element={adminOrSuperAdmin(<FormBuilder />)} />
          <Route path="/workflows/:id" element={adminOrSuperAdmin(<WorkflowEditor />)} />
          <Route path="/audit" element={adminOrSuperAdmin(<Audit />)} />
          <Route path="/address-verification" element={adminOnly(<AddressVerification />)} />

          <Route path="*" element={<Navigate to={isExoView ? '/reports' : '/dashboard'} replace />} />
        </Routes>
        </Suspense>
        </div>
      </div>
    </div>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const adding = new URLSearchParams(location.search).get('add') === '1';
  // SEC-06: don't flash the login form while we're still checking for an
  // existing session cookie.
  if (loading) return <RouteFallback />;
  return user && !adding ? <Navigate to={isExoPortalView(user) ? '/reports' : '/dashboard'} replace /> : children;
};

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <ConfirmProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/sso-callback" element={<SSOCallback />} />
            {/*
              /register removed. The page was never linked from anywhere, but
              the route existed and the API behind it created a working account
              for anyone who found it — which is how three accounts on a
              disposable-mail domain got in on 21 Aug 2026.
              New accounts come from HR, or from an admin, via an invite link.
            */}
            <Route path="/accept-invite" element={<AcceptInvite />} />
            {/* Public by design — the signed token in the URL is the credential.
                Not wrapped in PublicRoute: a signed-in approver must still be
                able to open the link from their inbox. */}
            <Route path="/approve-cost/:token" element={<CostApproval />} />
            <Route path="/" element={<PublicRoute><LandingRedirect /></PublicRoute>} />
            <Route path="/*" element={<AppLayout />} />
          </Routes>
        </BrowserRouter>
        </ConfirmProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
